import { generateClient } from "aws-amplify/api";
import {
  listEvents,
  listProducts,
  listEventProductAvailabilities,
  listDiscounts,
} from "../graphql/queries";

const client = generateClient();

/**
 * Fetch all tournaments/events from the database
 * Filters out events where registration has closed (handled in frontend for now)
 */
export const fetchTournaments = async () => {
  try {
    const response = await client.graphql({
      query: listEvents,
      variables: {
        limit: 100,
      },
    });

    const tournaments = response.data.listEvents.items;
    
    // Transform to match existing testingData structure if needed
    return tournaments.map((event) => ({
      id: event.id,
      name: event.name,
      city: event.city,
      state: event.state,
      dateString: event.dateString,
      numberOfDays: event.numberOfDays,
      sport: event.sport,
      gender: event.gender,
      link: event.link,
      imageAlt: event.imageAlt,
      imageSrc: event.imageSrc,
      startTimestamp: event.startTimestamp,
      closeRegistrationTimestamp: event.closeRegistrationTimestamp,
      singlePrice: event.singlePrice,
      doublePrice: event.doublePrice,
      localSalesTax: event.localSalesTax,
      isSoldOut: event.isSoldOut || false,
    }));
  } catch (error) {
    console.error("Error fetching tournaments:", error);
    throw error;
  }
};

/**
 * Fetch all products (packages and add-ons) from the database
 */
export const fetchProducts = async () => {
  try {
    const response = await client.graphql({
      query: listProducts,
      variables: {
        limit: 1000,
        filter: {
          stillOffered: { eq: true }, // Only fetch products still being offered
        },
      },
    });

    return response.data.listProducts.items;
  } catch (error) {
    console.error("Error fetching products:", error);
    throw error;
  }
};

/**
 * Fetch packages specifically (BUNDLE availability)
 */
export const fetchPackages = async () => {
  try {
    const response = await client.graphql({
      query: listProducts,
      variables: {
        limit: 1000,
        filter: {
          availability: { eq: "BUNDLE" },
          stillOffered: { eq: true },
        },
      },
    });

    return response.data.listProducts.items.map((product) => ({
      id: product.id,
      name: product.name,
      price: product.price,
      description: product.description,
      features: product.features || [],
      stillOffered: product.stillOffered,
      availability: product.availability,
      href: product.href || "#",
      discountText: product.subDescription || "",
    }));
  } catch (error) {
    console.error("Error fetching packages:", error);
    throw error;
  }
};

/**
 * Fetch add-ons specifically (ALA_CARTE availability)
 */
export const fetchAddOns = async () => {
  try {
    const response = await client.graphql({
      query: listProducts,
      variables: {
        limit: 1000,
        filter: {
          availability: { eq: "ALA_CARTE" },
          stillOffered: { eq: true },
        },
      },
    });

    return response.data.listProducts.items.map((product) => ({
      id: product.id,
      name: product.name,
      price: product.price,
      description: product.description,
      imageSrc: product.imageSrc,
      quantity: 0, // Default quantity for cart
      stillOffered: product.stillOffered,
      availability: product.availability,
    }));
  } catch (error) {
    console.error("Error fetching add-ons:", error);
    throw error;
  }
};

/**
 * Fetch event-product availability mappings
 */
export const fetchEventProductAvailability = async () => {
  try {
    const response = await client.graphql({
      query: listEventProductAvailabilities,
      variables: {
        limit: 10000, // This could be a large list
      },
    });

    return response.data.listEventProductAvailabilities.items.map((item) => ({
      id: item.id,
      eventId: item.eventId,
      productId: item.productId,
      isAvailable: item.isAvailable,
    }));
  } catch (error) {
    console.error("Error fetching event product availability:", error);
    throw error;
  }
};

/**
 * Fetch all discount codes
 */
export const fetchDiscounts = async () => {
  try {
    const response = await client.graphql({
      query: listDiscounts,
      variables: {
        limit: 1000,
      },
    });

    return response.data.listDiscounts.items.map((discount) => ({
      id: discount.id,
      code: discount.code,
      description: discount.description,
      type: discount.type,
      value: discount.value,
      expirationDate: discount.expirationDate,
      usageLimit: discount.usageLimit,
      eventId: discount.eventId,
      createdAt: discount.createdAt,
      updatedAt: discount.updatedAt,
      isShowable: discount.isShowable || false,
    }));
  } catch (error) {
    console.error("Error fetching discounts:", error);
    throw error;
  }
};

/**
 * Fetch all data needed for the reservations page
 */
export const fetchAllReservationData = async () => {
  try {
    const [tournaments, packages, addOns, eventProductAvailability, discounts] =
      await Promise.all([
        fetchTournaments(),
        fetchPackages(),
        fetchAddOns(),
        fetchEventProductAvailability(),
        fetchDiscounts(),
      ]);

    return {
      tournaments,
      packages,
      addOns,
      eventProductAvailability,
      discounts,
    };
  } catch (error) {
    console.error("Error fetching all reservation data:", error);
    throw error;
  }
};
